import { Box, LinearProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { singleMessages } from 'utils/global-messages';

interface FormSubmitRowProps extends React.PropsWithChildren {
  onCancel?: () => void;
  sx?: any;
  loading?: boolean;
  disabled?: boolean;
  dirty?: boolean;
  cancelText?: string;
  submitText?: string;
  hideSubmitButton?: boolean;
  enableIfNotDirty?: boolean;
  cancelAlwaysEnabled?: boolean;
  showLoadingAtBottom?: boolean;
  formId?: string;
}

export const FormSubmitRow: FC<FormSubmitRowProps> = ({
  onCancel,
  loading,
  sx,
  disabled,
  dirty,
  cancelText,
  submitText,
  hideSubmitButton,
  enableIfNotDirty,
  cancelAlwaysEnabled,
  showLoadingAtBottom,
  formId,
  children,
}) => {
  const form = useFormContext();
  const { errors, isDirty: dirtyFromForm, isSubmitting } = form?.formState ?? {};

  const hasErrors = errors && Object.keys(errors).length > 0;
  const isDirty = dirtyFromForm != undefined ? dirtyFromForm : dirty;
  const isLoading = isSubmitting || loading;
  const cancelDisabled = cancelAlwaysEnabled ? false : isLoading || (!isDirty && !enableIfNotDirty);

  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      {!showLoadingAtBottom && isLoading && <LinearProgress sx={{ width: '100%' }} />}
      <Stack direction="row-reverse" spacing={2} sx={sx} alignItems="center">
        {!hideSubmitButton && (
          <Button
            type="submit"
            form={formId}
            variant="contained"
            disabled={isLoading || (!isDirty && !enableIfNotDirty) || disabled || hasErrors}
            sx={{ height: '40px', paddingX: 2, flexShrink: 0 }}
          >
            {submitText ?? <FormattedMessage {...singleMessages.submit} />}
          </Button>
        )}
        {!!onCancel && (
          <Button
            disabled={cancelDisabled}
            onClick={onCancel}
            variant="contained"
            color="secondary"
            sx={{ height: '40px', paddingX: 2, flexShrink: 0 }}
          >
            {cancelText ?? <FormattedMessage {...singleMessages.cancel} />}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }}>{children}</Box>
      </Stack>
      {showLoadingAtBottom && isLoading && <LinearProgress sx={{ width: '100%' }} />}
    </Stack>
  );
};
